import { useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Button } from "@twilio-paste/core/button";
import { AttachIcon } from "@twilio-paste/icons/esm/AttachIcon";

import { AppState } from "../store/definitions";
import { hiddenInputStyles } from "./styles/AttachFileButton.styles";
import { validateFiles } from "../utils/validateFiles";
import { buildAcceptAttribute } from "../utils/fileTypes";
import { attachFiles } from "../store/actions/genericActions";

export const AttachFileButton = ({ textAreaRef }: { textAreaRef?: React.RefObject<HTMLTextAreaElement | null> }) => {
    /*
     * Sem anotação explícita, com o genérico no `useRef`. O `@types/react` 19 mudou o `RefObject<T>`
     * para incluir `null`, e as duas formas óbvias de "corrigir" quebram de um lado ou do outro:
     * anotar `RefObject<HTMLInputElement>` falha no 19 (o `useRef(null)` devolve `RefObject<null>`) e
     * anotar `RefObject<HTMLInputElement | null>` falha no 18 (o `ref=` do JSX exige sem `null`).
     * Medido nos dois majors: esta forma passa em ambos.
     */
    const fileInputRef = useRef<HTMLInputElement>(null);
    const { attachedFiles, fileAttachmentConfig } = useSelector((state: AppState) => ({
        attachedFiles: state.chat.attachedFiles || [],
        fileAttachmentConfig: state.config.fileAttachment
    }));

    const dispatch = useDispatch();

    const onFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const selectedFiles = event.target.files && Array.from(event.target.files);
        if (selectedFiles) {
            const validFiles = validateFiles(selectedFiles, dispatch, attachedFiles, fileAttachmentConfig);
            dispatch(attachFiles(validFiles));
        }
        if (fileInputRef.current) {
            fileInputRef.current.value = "";
        }
        textAreaRef?.current?.focus();
    };

    return (
        <Button variant="secondary_icon" size="icon_small" onClick={() => fileInputRef.current?.click()}>
            <input
                style={hiddenInputStyles}
                onChange={onFileChange}
                type="file"
                /* Extensões + MIME: só com extensão o seletor do Chrome no Android
                   esconde imagens válidas (o Google Fotos não expõe extensão). */
                accept={buildAcceptAttribute(fileAttachmentConfig?.acceptedExtensions) || undefined}
                ref={fileInputRef}
                multiple
            />
            <AttachIcon decorative={false} title="Anexar arquivo" />
        </Button>
    );
};
